import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  inject,
  OnDestroy,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  EBannerHorizontalAlignment,
  EBannerVerticalAlignment,
  DEFAULT_BANNER,
  IBanner,
  ERelationType,
  EEntityType,
} from '@web/dtos';
import { map, Observable, Subscription } from 'rxjs';
import { AccessibilityBannerComponent } from '../../../components/layout/accessibility-banner/accessibility-banner.component';
import { BannerComponent } from '../../../components/data-display/banner/banner.component';
import { ButtonComponent } from '../../../components/feedback/button/button.component';
import { IDialogData } from '../../../components/dialogs/dialog';
import { ResetChangesDialogComponent } from '../../../components/dialogs/reset-changes-dialog/reset-changes-dialog.component';
import { ButtonGroupComponent } from '../../../components/data-input/form-inputs/button-group/button-group.component';
import { ColorPickerInputComponent } from '../../../components/data-input/form-inputs/color-picker-input/color-picker-input.component';
import { DropDownComponent } from '../../../components/data-input/form-inputs/drop-down/drop-down.component';
import { FontSelectComponent } from '../../../components/data-input/form-inputs/font-select/font-select.component';
import { ImageSelectComponent } from '../../../components/data-input/form-inputs/image-select/image-select.component';
import { LanguageSwitcherComponent } from '../../../components/data-input/form-inputs/language-switcher/language-switcher.component';
import { NumberInputComponent } from '../../../components/data-input/form-inputs/number-input/number-input.component';
import { ToggleComponent } from '../../../components/data-input/form-inputs/toggle/toggle.component';
import { TranslationAccessibilityTextInputComponent } from '../../../components/data-input/form-inputs/translation-accessibility-text-input/translation-accessibility-text-input.component';
import { TranslationTextInputComponent } from '../../../components/data-input/form-inputs/translation-text-input/translation-text-input.component';
import { LoadingAnimationComponent } from '../../../components/feedback/loading-animation/loading-animation.component';
import { PreviewComponent } from '../../../components/feedback/preview/preview.component';
import { RevealFormErrorsButtonComponent } from '../../../components/feedback/reveal-form-errors-button/reveal-form-errors-button.component';
import { SectionHeaderActionsComponent } from '../../../components/layout/section/section-header/section-header-actions/section-header-actions.component';
import { SectionHeaderComponent } from '../../../components/layout/section/section-header/section-header.component';
import { SectionHeadlineComponent } from '../../../components/layout/section/section-header/section-headline/section-headline.component';
import { SectionComponent } from '../../../components/layout/section/section.component';
import {
  ECMSFontColors,
  ECMSFontSize,
  ECMSFontSizeBig,
  ECMSFontSizeSmall,
} from '../../../constants/buttonGroups.enums';
import { IBannerForm } from '../../../entities/banner-entity';
import { BannerFormsFactoryService } from '../../../factory/banner/banner-forms-factory.service';
import { EnumToButtonOptionArrayPipe } from '../../../pipes/enum-to-button-option-array/enum-to-button-option-array.pipe';
import { BannerStore } from '../../../services/api/banner/banner-store.service';
import { ButtonGroupSupportService } from '../../../services/support/button-group-support/button-group-support.service';
import { FormTracking } from '../form-tracking/form-tracking.component';
import { ElementFocusService } from '../../../services/support/element-focus-support/element-focus-support.service';
import { DialogService } from '../../../services/support/dialog/dialog.service';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';

@Component({
  selector: 'aw-banner-page',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    SectionComponent,
    LanguageSwitcherComponent,
    SectionHeadlineComponent,
    SectionHeaderComponent,
    SectionHeaderActionsComponent,
    ButtonComponent,
    AccessibilityBannerComponent,
    ToggleComponent,
    ImageSelectComponent,
    NumberInputComponent,
    TranslationAccessibilityTextInputComponent,
    TranslationTextInputComponent,
    ButtonGroupComponent,
    EnumToButtonOptionArrayPipe,
    DropDownComponent,
    ColorPickerInputComponent,
    FontSelectComponent,
    LoadingAnimationComponent,
    RevealFormErrorsButtonComponent,
    PreviewComponent,
  ],
  templateUrl: './banner-page.component.html',
  styles: ``,
})
export class BannerPageComponent
  extends FormTracking<IBannerForm>
  implements OnDestroy
{
  private readonly elementFocusService: ElementFocusService =
    inject(ElementFocusService);
  readonly buttonGroupSupportService: ButtonGroupSupportService = inject(
    ButtonGroupSupportService
  );
  readonly bannerStore: BannerStore = inject(BannerStore);
  readonly bannerFactory: BannerFormsFactoryService = inject(
    BannerFormsFactoryService
  );
  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  private readonly dialogService: DialogService = inject(DialogService);
  protected banner: IBanner | undefined;
  public bannerForm: FormGroup<IBannerForm> = this.bannerFactory.buildForm(
    this.bannerStore.banner()
  );
  public languageForm: FormGroup = new FormGroup({
    language: new FormControl('DE', [Validators.required]),
  });

  protected readonly EntityType: typeof EEntityType = EEntityType;
  // DataSources for Components
  protected readonly fontSizes: typeof ECMSFontSize = ECMSFontSize;
  protected readonly headlineFontSizes: typeof ECMSFontSizeBig =
    ECMSFontSizeBig;
  protected readonly subHeadlineFontSizes: typeof ECMSFontSizeSmall =
    ECMSFontSizeSmall;
  protected readonly fontColors: typeof ECMSFontColors = ECMSFontColors;
  protected readonly TextRelationType: typeof ERelationType = ERelationType;

  public readonly verticalAlignments: typeof EBannerVerticalAlignment =
    EBannerVerticalAlignment;
  public readonly horizontalAlignments: typeof EBannerHorizontalAlignment =
    EBannerHorizontalAlignment;

  public readonly headlineFontSizeBtn: FormControl<string> = new FormControl();
  public readonly subHeadlineFontSizeBtn: FormControl<string> =
    new FormControl();
  public readonly textColorBtn: FormControl<string> = new FormControl();
  public readonly textFontBtn: FormControl<string> = new FormControl();

  private readonly subscription: Subscription = new Subscription();

  protected readonly BannerComponent: typeof BannerComponent = BannerComponent;
  previewData: WritableSignal<IBanner[]> = signal([]);

  constructor() {
    super();
    effect((): void => {
      this.banner = this.deepCopyService.createDeepCopy(
        this.bannerStore.banner()
      );
      this.bannerForm = this.bannerFactory.buildForm(
        this.deepCopyService.createDeepCopy(this.bannerStore.banner())
      );
      this.previewData = signal([this.banner]);
      this.initFormTracking(this.bannerForm);
      this.setInitialButtonStates(this.bannerStore.banner());
      this.createActivitySubscription();
      this.bannerForm.controls.isImageActive.updateValueAndValidity();
      this.bannerForm.controls.isTextActive.updateValueAndValidity();
      this.createPreviewListener();
    });
    effect(() => {
      this.elementFocusService.focusElement(
        !this.bannerStore.isLoading() && !!this.bannerStore.banner()
      );
    });
    window.onbeforeunload = (event: BeforeUnloadEvent): void => {
      if (this.bannerForm.dirty) {
        event.preventDefault();
      }
    };
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private createPreviewListener() {
    this.subscription.add(
      this.bannerForm.valueChanges.subscribe(value => {
        this.previewData.set([this.bannerFactory.buildEntity(this.bannerForm)]);
      })
    );
  }

  createActivitySubscription(): void {
    this.subscription.add(
      this.bannerForm.controls.isImageActive.valueChanges.subscribe(
        (isActive: boolean): void => {
          if (isActive) {
            this.bannerForm.controls.imageHeight.enable();
            this.bannerForm.controls.accessibleImage.enable();
            this.bannerForm.controls.isTextActive.enable();
          } else {
            this.bannerForm.controls.imageHeight.disable();
            this.bannerForm.controls.accessibleImage.disable();
            this.bannerForm.controls.isTextActive.disable();
            this.bannerForm.controls.isTextActive.setValue(false);
          }
        }
      )
    );
    this.subscription.add(
      this.bannerForm.controls.isTextActive.valueChanges.subscribe(
        (isActive: boolean): void => {
          if (isActive) {
            this.headlineFontSizeBtn.enable();
            this.subHeadlineFontSizeBtn.enable();
            this.textColorBtn.enable();
            this.textFontBtn.enable();
            this.bannerForm.controls.headlines.enable();
            this.bannerForm.controls.subHeadlines.enable();
            this.bannerForm.controls.textAlignmentVertical.enable();
            this.bannerForm.controls.textAlignmentHorizontal.enable();
          } else {
            this.headlineFontSizeBtn.disable();
            this.subHeadlineFontSizeBtn.disable();
            this.textColorBtn.disable();
            this.textFontBtn.disable();
            this.bannerForm.controls.textColor.disable();
            this.bannerForm.controls.textColor.disable();
            this.bannerForm.controls.textFont.disable();
            this.bannerForm.controls.headlines.disable();
            this.bannerForm.controls.headlineFontSize.disable();
            this.bannerForm.controls.subHeadlines.disable();
            this.bannerForm.controls.subHeadlineFontSize.disable();
            this.bannerForm.controls.textAlignmentVertical.disable();
            this.bannerForm.controls.textAlignmentHorizontal.disable();
          }
        }
      )
    );
  }

  onHeadlinesFontSizeChange(value: string): void {
    switch (value) {
      case ECMSFontSizeBig.Benutzerdefiniert:
        this.bannerForm.controls.headlineFontSize.enable();
        break;
      case ECMSFontSizeBig.Standard:
        this.bannerForm.controls.headlineFontSize.setValue(
          DEFAULT_BANNER.headlineFontSize
        );
        this.bannerForm.controls.headlineFontSize.disable();
        break;
    }
    this.bannerForm.markAsDirty();
  }
  onSubHeadlinesFontSizeChange(value: string): void {
    switch (value) {
      case ECMSFontSizeSmall.Benutzerdefiniert:
        this.bannerForm.controls.subHeadlineFontSize.enable();
        break;
      case ECMSFontSizeSmall.Standard:
        this.bannerForm.controls.subHeadlineFontSize.setValue(
          DEFAULT_BANNER.subHeadlineFontSize
        );
        this.bannerForm.controls.subHeadlineFontSize.disable();
        break;
    }
    this.bannerForm.markAsDirty();
  }

  onFontColorChange(value: string): void {
    switch (value) {
      case ECMSFontColors.Benutzerdefiniert:
        this.bannerForm.controls.textColor.enable();
        break;
      default:
        this.bannerForm.controls.textColor.setValue(value);
        this.bannerForm.controls.textColor.disable();
        break;
    }
    this.bannerForm.markAsDirty();
  }

  onFontChange(value: string): void {
    switch (value) {
      case ECMSFontSize.Benutzerdefiniert:
        this.bannerForm.controls.textFont.enable();
        break;
      default:
        this.bannerForm.controls.textFont.setValue(DEFAULT_BANNER.textFont);
        this.bannerForm.controls.textFont.disable();
        break;
    }
    this.bannerForm.markAsDirty();
  }

  save(): boolean {
    if (!this.bannerForm.valid) {
      this.bannerForm.markAllAsTouched();
      return false;
    }
    this.bannerStore.save(this.bannerFactory.buildEntity(this.bannerForm));
    this.bannerForm.markAsPristine();
    return true;
  }

  private setInitialButtonStates(banner: IBanner) {
    if (banner.headlineFontSize.toString() !== ECMSFontSizeBig.Standard) {
      this.headlineFontSizeBtn.setValue(ECMSFontSizeBig.Benutzerdefiniert);
    } else {
      this.headlineFontSizeBtn.setValue(ECMSFontSizeBig.Standard);
    }
    this.onHeadlinesFontSizeChange(this.headlineFontSizeBtn.value);

    if (banner.subHeadlineFontSize.toString() !== ECMSFontSizeSmall.Standard) {
      this.subHeadlineFontSizeBtn.setValue(ECMSFontSizeSmall.Benutzerdefiniert);
    } else {
      this.subHeadlineFontSizeBtn.setValue(ECMSFontSizeSmall.Standard);
    }
    this.onSubHeadlinesFontSizeChange(this.subHeadlineFontSizeBtn.value);

    this.textColorBtn.setValue(
      this.buttonGroupSupportService.findValueOnEnum(
        banner.textColor.toString(),
        this.fontColors,
        ECMSFontColors.Benutzerdefiniert
      )
    );
    this.onFontColorChange(this.textColorBtn.value);

    this.textFontBtn.setValue(
      banner.textFont ? ECMSFontSize.Benutzerdefiniert : ECMSFontSize.Standard
    );
    this.onFontChange(this.textFontBtn.value);
  }

  resetFormChanges() {
    this.bannerStore.reload();
  }

  public handleAbortChangesDialog(): Observable<boolean> {
    const dialogRef = this.dialogService.openDialog<IDialogData>(
      ResetChangesDialogComponent,
      {
        headline: this.cmsTextProvider.shared.revertChanges,
        width: '600px',
        icon: 'warning',
        iconBackgroundColor: '#eed202',
      }
    );
    dialogRef.closed.subscribe(result => {
      if (result != undefined && result.submit) {
        if (result.acceptChanges) {
          this.save();
        } else {
          this.resetFormChanges();
        }
      }
    });
    return dialogRef.closed.pipe(map(result => result?.submit ?? false));
  }
}
